import React, { useEffect, useState } from "react";

import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ArrayParam, NumberParam, StringParam, useQueryParams, withDefault } from "use-query-params";

import { Key, SorterResult, TableCurrentDataSource, TablePaginationConfig } from "antd/lib/table/interface";

import { Page } from "../../components/page";
import { PageHeader } from "../../components/page_header";
import { DateRange } from "../../components/selectors";
import { resolutionOptions, ResolutionSelector } from "../../components/selectors/resolution_selector";
import { ResolutionType } from "../../store/common_types";
import {
  integrationErrorsChartFetchRequest,
  integrationErrorsFetchRequest,
  integrationErrorsSamplesFetchRequest,
} from "../../store/integration_errors/actions";
import { IntegrationErrorItem, IntegrationErrorType } from "../../store/integration_errors/types";
import { RootState } from "../../store/root_reducer";
import { enumParams } from "../../utils/enum_params";
import { MomentParam } from "../../utils/moment_param";
import { rangeResolutionLimit, resolutionUnitMap } from "../../utils/resolution";

import { IntegrationErrorsChart, IntegrationErrorsTable, optionsErrorsType } from "./components";

const orderingFields = ["timestamp", "-timestamp"];

export const IntegrationErrorsPage: React.FC = () => {
  const { t } = useTranslation("integration_errors_page");

  const integrationErrors = useSelector((state: RootState) => state.IntegrationErrors);
  const gateId = useSelector((state: RootState) => state.User.selectedGate.id);

  const [refresh, setRefresh] = useState<boolean>(false);

  const [commonState, setCommonState] = useQueryParams({
    from: withDefault(MomentParam, moment.utc().startOf("day").subtract(1, "week").add(1, "day")),
    to: withDefault(MomentParam, moment.utc().startOf("day")),
    resolution: withDefault(enumParams<ResolutionType>(StringParam, resolutionOptions), "10min"),
  });
  const [tableState, setTableState] = useQueryParams({
    pageSize: withDefault(NumberParam, 10),
    page: withDefault(NumberParam, 1),
    ordering: withDefault(enumParams<string>(StringParam, orderingFields), "-timestamp"),
    errorsType: withDefault(
      enumParams<IntegrationErrorType[], IntegrationErrorType>(ArrayParam, optionsErrorsType),
      []
    ),
  });

  useEffect(() => {
    setCommonState((state) => state, "replaceIn");
    setTableState((state) => state, "replaceIn");
  }, [setCommonState, setTableState]);

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(integrationErrorsFetchRequest({ ...commonState, ...tableState, gateId }));
    dispatch(integrationErrorsSamplesFetchRequest({ gateId }));
  }, [dispatch, commonState, tableState, gateId, refresh]);
  useEffect(() => {
    dispatch(integrationErrorsChartFetchRequest({ ...commonState, gateId }));
  }, [dispatch, commonState, gateId, refresh]);

  const handleReload = () => setRefresh((state) => !state);
  const handleSelectDatesRange = (dateRange: DateRange) => {
    const rangeDiffDays = dateRange.to.diff(dateRange.from, "days");
    setCommonState((state) => ({
      ...state,
      ...dateRange,
      resolution: rangeResolutionLimit(rangeDiffDays, state.resolution),
    }));
    setTableState((state) => ({ ...state, page: 1 }), "replaceIn");
  };

  const handleTableChange = (
    { current, pageSize }: TablePaginationConfig,
    filters: Record<string, (Key | boolean)[] | null>,
    sorter: SorterResult<IntegrationErrorItem> | SorterResult<IntegrationErrorItem>[],
    { action }: TableCurrentDataSource<IntegrationErrorItem>
  ) => {
    switch (action) {
      case "paginate": {
        if (current && pageSize)
          setTableState((state) => ({ ...state, page: state.pageSize !== pageSize ? 1 : current, pageSize }));
        break;
      }
      case "sort":
        if (!Array.isArray(sorter)) {
          const { field, order } = sorter;
          const ordering = `${order === "descend" ? "-" : ""}${field}`;
          setTableState((state) => ({ ...state, ordering }));
        }
        break;
      case "filter": {
        const errorTypeFilter = (filters.errorType || []) as IntegrationErrorType[];
        if (tableState.errorsType.toString() !== errorTypeFilter.toString()) {
          setTableState((state) => ({ ...state, errorsType: errorTypeFilter, page: 1 }));
        }
        break;
      }
      default:
        break;
    }
  };

  return (
    <Page title={t("page.title")} description={t("page.description")} reportTTL={60}>
      <PageHeader
        onReload={handleReload}
        onDateRangeSelect={handleSelectDatesRange}
        dateRange={commonState}
        isLoading={integrationErrors.dataIsLoading || integrationErrors.chartDataIsLoading}
      >
        <ResolutionSelector
          resolution={commonState.resolution}
          rangeDiffDay={commonState.to.diff(commonState.from, "days")}
          onSelect={(resolution) => {
            setCommonState((state) => ({ ...state, resolution }));
            setTableState((state) => ({ ...state, page: 1 }));
          }}
        />
      </PageHeader>
      <IntegrationErrorsChart
        items={integrationErrors.chartItems}
        loading={integrationErrors.chartDataIsLoading}
        rangeFrom={commonState.from}
        rangeTo={commonState.to}
        resolution={commonState.resolution}
      />
      <IntegrationErrorsTable
        errorTypeFilter={tableState.errorsType}
        timestampFmt={resolutionUnitMap[commonState.resolution].tooltipFormat as string}
        loading={integrationErrors.dataIsLoading}
        total={integrationErrors.count}
        dataSource={integrationErrors.items}
        currentPage={tableState.page}
        pageSize={tableState.pageSize}
        onChange={handleTableChange}
        ordering={tableState.ordering}
      />
    </Page>
  );
};
